import React, { useState, useEffect } from 'react';
import { FaProjectDiagram, FaExternalLinkAlt, FaSearch } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const ProjectsPage = () => {
  usePageTitle();
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    supabase.from('projects').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { setProjects(data || []); setLoading(false); });
  }, []);

  const filtered = projects.filter(p => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const normalizeUrl = (url) => {
    if (!url) return url;
    if (!/^https?:\/\//i.test(url)) return 'https://' + url;
    return url;
  };

  const statusColor = (s) => {
    if (s === 'Berjalan') return 'bg-green-100 text-green-700 border-green-200';
    if (s === 'Selesai') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (s === 'Rencana') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const statusDot = (s) => {
    if (s === 'Berjalan') return 'bg-green-500';
    if (s === 'Selesai') return 'bg-blue-500';
    if (s === 'Rencana') return 'bg-amber-500';
    return 'bg-gray-500';
  };

  const counts = {
    all: projects.length,
    Berjalan: projects.filter(p => p.status === 'Berjalan').length,
    Selesai: projects.filter(p => p.status === 'Selesai').length,
    Rencana: projects.filter(p => p.status === 'Rencana').length,
  };

  const tabs = [
    { key: 'all', label: t('projectsPage.all') },
    { key: 'Berjalan', label: t('projectsPage.ongoing') },
    { key: 'Selesai', label: t('projectsPage.completed') },
    { key: 'Rencana', label: t('projectsPage.planned') },
  ];

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FaProjectDiagram className="text-gold-400 text-4xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">{t('projectsPage.title')}</h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t('projectsPage.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder={t('projectsPage.searchPlaceholder')}
            />
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setFilterStatus(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                filterStatus === t.key
                  ? 'bg-primary-600 text-white shadow'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {t.label} ({counts[t.key]})
            </button>
          ))}
        </div>

        {/* Project Cards */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FaProjectDiagram className="text-5xl mx-auto mb-4 opacity-50" />
            <p>{t('projectsPage.noProjects')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(p => (
              <div
                key={p.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all overflow-hidden group"
              >
                {/* Banner Image or Top accent */}
                {p.banner_image ? (
                  <img src={p.banner_image} alt={p.title} className="w-full h-48 object-cover" />
                ) : (
                  <div className={`h-1.5 ${statusDot(p.status)}`}></div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${statusColor(p.status)}`}>
                      {p.status}
                    </span>
                  </div>

                  {p.description && (
                    <p className="text-sm text-gray-500 mb-4 line-clamp-3">{p.description}</p>
                  )}

                  {p.link && (
                    <a
                      href={normalizeUrl(p.link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm px-4 py-2.5 rounded-lg transition-colors shadow-sm hover:shadow"
                    >
                      <FaExternalLinkAlt className="text-xs" />
                      {t('projectsPage.visitWebsite')}
                    </a>
                  )}

                  {p.created_at && (
                    <p className="text-xs text-gray-400 mt-4">
                      {new Date(p.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
